import { getApp, getApps, initializeApp } from 'firebase/app';
import { AppCheck, getToken } from 'firebase/app-check';
import {
  EmailAuthProvider,
  PhoneAuthProvider,
  RecaptchaVerifier,
  getAuth,
  signInWithCredential,
  signInWithCustomToken,
  signInWithPhoneNumber,
  signOut,
  sendPasswordResetEmail,
  updatePhoneNumber,
  verifyPasswordResetCode,
  confirmPasswordReset,
  sendSignInLinkToEmail,
} from 'firebase/auth';
import { Logger, LoggerConstant } from '@/hooks/useLogging';
import { firebaseConfig } from './env';

export {};

declare global {
  interface Window {
    recaptchaVerifier: any;
    verificationId: any;
    recaptchaWidgetId: any;
    confirmationResult: any;
  }
}

// initial firebase
const app = !getApps().length ? initializeApp(firebaseConfig) : getApp();
const auth = getAuth(app);

//app check
let AppCheckInstance: AppCheck | null = null;
if (typeof window !== 'undefined' && !AppCheckInstance) {
  import('firebase/app-check').then(async (firebaseAppCheck) => {
    const appCheckKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY_APP_CHECK || '';
    AppCheckInstance = firebaseAppCheck.initializeAppCheck(app, {
      provider: new firebaseAppCheck.ReCaptchaV3Provider(appCheckKey),
      isTokenAutoRefreshEnabled: true,
    });
  });
}
export const getAppCheckToken = async () => {
    Logger.info({
      module: LoggerConstant.Firebase,
      funcName: 'Get App Check Token',
      params: {},
    });
  try {
    if (typeof window !== 'undefined' && AppCheckInstance) {
      const appCheckTokenResponse = await getToken(AppCheckInstance, false);

      const token = appCheckTokenResponse?.token;
      Logger.info({
        module: LoggerConstant.Firebase,
        funcName: 'Get App Check Token Success',
        params: { token },
      });
      return token;
    }
    return null;
  } catch (error) {
    Logger.error({
      module: LoggerConstant.Firebase,
      funcName: 'Get App Check Token Fail',
      error: { error },
    });
    throw error;
  }
};

export const sendVerificationCode = async (phoneNumber: string) => {
  const phoneMasked = phoneNumber.replace(/.(?=.{4})/g, '*'); // Mask the phone number for security reasons
  try {
    auth.useDeviceLanguage();
    if (window?.recaptchaVerifier) {
      Logger.info({
        module: LoggerConstant.Firebase,
        funcName: 'Render Recaptcha',
        params: {},
      });
      const widgetId = await window.recaptchaVerifier.render();
      Logger.info({
        module: LoggerConstant.Firebase,
        funcName: 'Render Recaptcha Success',
        params: { widgetId },
      });
      window.recaptchaWidgetId = widgetId;
    }
    // clear window variable
    if (window?.confirmationResult) {
      delete window.confirmationResult;
    }
    if (window?.verificationId) {
      delete window.verificationId;
    }
    if (!window?.recaptchaVerifier) {
      Logger.info({
        module: LoggerConstant.Firebase,
        funcName: 'Create Recaptcha',
        params: {},
      });
      window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recapcha-container', {
        size: 'invisible',
      });
      Logger.info({
        module: LoggerConstant.Firebase,
        funcName: 'Create Recaptcha Success',
        params: {},
      });
    }
    if (window.recaptchaVerifier) {
      const user = auth?.currentUser;
      if (user?.phoneNumber) {
        Logger.info({
          module: LoggerConstant.Firebase,
          funcName: 'Send Verification Code',
          params: { phoneNumber: phoneMasked },
        });
        window.confirmationResult = await signInWithPhoneNumber(auth, `${phoneNumber}`, window.recaptchaVerifier);
        Logger.info({
          module: LoggerConstant.Firebase,
          funcName: 'Send Verification Code Success',
          params: { phoneNumber: phoneMasked },
        });
        return window.confirmationResult;
      }
      const provider = new PhoneAuthProvider(auth);
      Logger.info({
        module: LoggerConstant.Firebase,
        funcName: 'Send Verification Code',
        params: { phoneNumber: phoneMasked },
      });
      window.verificationId = await provider.verifyPhoneNumber(`${phoneNumber}`, window.recaptchaVerifier);
      Logger.info({
        module: LoggerConstant.Firebase,
        funcName: 'Send Verification Code Success',
        params: { phoneNumber: phoneMasked },
      });
    }
    return window.verificationId;
  } catch (error: any) {
    Logger.error({
      module: LoggerConstant.Firebase,
      funcName: 'Send Verification Code Fail',
      error: { error, phoneNumber: phoneMasked },
    });
    throw error?.code;
  }
};

export const verificationCodeAndUpdatePhone = async (code: string) => {
  try {
    if (auth?.currentUser) {
      Logger.info({
        module: LoggerConstant.Firebase,
        funcName: 'Verify Code and Update Phone',
        params: {},
      });
      const phoneCredential = PhoneAuthProvider.credential(window.verificationId, code);
      await updatePhoneNumber(auth.currentUser, phoneCredential);
      Logger.info({
        module: LoggerConstant.Firebase,
        funcName: 'Verify Code and Update Phone Success',
        params: {},
      });
      delete window.verificationId;
    }
  } catch (error: any) {
    Logger.error({
      module: LoggerConstant.Firebase,
      funcName: 'Verify Code and Update Phone Fail',
      error: { error },
    });
    throw error.code;
  }
};

export const sendSignInLinkToEmailAddress = async (email: string) => {
  const actionCodeSettings = {
    // URL you want to redirect back to. The domain (www.example.com) for this
    // URL must be in the authorized domains list in the Firebase Console.
    url: `https://${process.env.REACT_APP_CONTINUE_URL_SIGNIN_MAIL}/register`,
    // This must be true.
    handleCodeInApp: true,
  };
  Logger.info({
    module: LoggerConstant.Firebase,
    funcName: 'Send Sign In Link To Email',
    params: { email: email.replace(/.(?=.{4})/g, '*') }, // Mask the email for security reasons
  });
  return sendSignInLinkToEmail(auth, email, actionCodeSettings).then(() => {
    window.localStorage.setItem('emailForSignIn', email);
  });
};

export const verificationPhoneCode = async (code: string) => {
  try {
    if (window?.confirmationResult) {
      Logger.info({
        module: LoggerConstant.Firebase,
        funcName: 'Verify Phone Code',
        params: {},
      });
      await window?.confirmationResult.confirm(code);
      Logger.info({
        module: LoggerConstant.Firebase,
        funcName: 'Verify Phone Code Success',
        params: {},
      });
      delete window.confirmationResult;
    }
  } catch (error: any) {
    Logger.error({
      module: LoggerConstant.Firebase,
      funcName: 'Verify Phone Code Fail',
      error: { error },
    });
    throw error.code;
  }
};

export const firebaseLogin = async (email: string, password: string) => {
  const emailMasked = email.replace(/.(?=.{4})/g, '*'); // Mask the email for security reasons
  try {
    Logger.info({
      module: LoggerConstant.Firebase,
      funcName: 'Login With Email and Password',
      params: { email: emailMasked },
    });
    const emailCredential = EmailAuthProvider.credential(email, password);
    const data = await signInWithCredential(auth, emailCredential);
    Logger.info({
      module: LoggerConstant.Firebase,
      funcName: 'Login With Email and Password Success',
      params: { email: emailMasked },
    });
    if (data) {
      return {
        userId: data.user?.uid,
        accessToken: await data.user?.getIdToken(),
        refreshToken: data.user?.refreshToken,
      };
    }
  } catch (error) {
    Logger.error({
      module: LoggerConstant.Firebase,
      funcName: 'Login With Email and Password Fail',
      error: { error, email: emailMasked },
    });
    throw error;
  }
};

export const signInWithEmailLink = async (link: string) => {
  try {
    Logger.info({
      module: LoggerConstant.Firebase,
      funcName: 'Sign In With Email Link',
      params: {},
    });
    const email = window.localStorage.getItem('emailForSignIn');
    if (!email) {
      return;
    }
    const emailMasked = email.replace(/.(?=.{4})/g, '*'); // Mask the email for security reasons
    Logger.info({
      module: LoggerConstant.Firebase,
      funcName: 'Sign In With Email Link Success',
      params: { email: emailMasked },
    });
    const credential = EmailAuthProvider.credentialWithLink(email, link);
    const data = await signInWithCredential(auth, credential);
    Logger.info({
      module: LoggerConstant.Firebase,
      funcName: 'Sign In With Email Link Success',
      params: { email: emailMasked },
    });
    if (data) {
      return {
        userId: data.user?.uid,
        accessToken: await data.user?.getIdToken(),
        refreshToken: data.user?.refreshToken,
      };
    }
  } catch (error) {
    Logger.error({
      module: LoggerConstant.Firebase,
      funcName: 'Sign In With Email Link Fail',
      error: { error },
    });
    throw error;
  }
};

export const sendPasswordToResetEmail = async (email: string) => {
  try {
    const emailMasked = email.replace(/.(?=.{4})/g, '*'); // Mask the email for security reasons
    Logger.info({
      module: LoggerConstant.Firebase,
      funcName: 'Send Password Reset Email',
      params: { email: emailMasked },
    });
    await sendPasswordResetEmail(auth, email, {
      url: window.location.origin + '/reset-password',
    });
    Logger.info({
      module: LoggerConstant.Firebase,
      funcName: 'Send Password Reset Email Success',
      params: { email: emailMasked },
    });
  } catch (error) {
    Logger.error({
      module: LoggerConstant.Firebase,
      funcName: 'Send Password Reset Email Fail',
      error: { error },
    });
    throw error;
  }
};

export const verifyPasswordResetCodeEmail = async (code: string) => {
  try {
    Logger.info({
      module: LoggerConstant.Firebase,
      funcName: 'Verify Password Reset Code',
      params: {},
    });
    const response = await verifyPasswordResetCode(auth, code);
    Logger.info({
      module: LoggerConstant.Firebase,
      funcName: 'Verify Password Reset Code Success',
      params: {},
    });
    return response;
  } catch (error) {
    Logger.error({
      module: LoggerConstant.Firebase,
      funcName: 'Verify Password Reset Code Fail',
      error: { error },
    });
    throw error;
  }
};

export const updateNewPassword = async (code: string, newPassword: string) => {
  try {
    Logger.info({
      module: LoggerConstant.Firebase,
      funcName: 'Update New Password',
      params: {},
    });
    await confirmPasswordReset(auth, code, newPassword);
    Logger.info({
      module: LoggerConstant.Firebase,
      funcName: 'Update New Password Success',
      params: {},
    });
  } catch (error) {
    Logger.error({
      module: LoggerConstant.Firebase,
      funcName: 'Update New Password Fail',
      error: { error },
    });
    throw error;
  }
};

export const signInWithCustomTokens = async (token: string) => {
  try {
    Logger.info({
      module: LoggerConstant.Firebase,
      funcName: 'Sign In With Custom Token',
      params: {},
    });
    const data = await signInWithCustomToken(auth, token);
    Logger.info({
      module: LoggerConstant.Firebase,
      funcName: 'Sign In With Custom Token Success',
      params: {},
    });
    if (data) {
      return {
        userId: data.user?.uid,
        accessToken: await data.user?.getIdToken(),
        refreshToken: data.user?.refreshToken,
        phoneNumber: data.user?.phoneNumber,
      };
    }
  } catch (error) {
    Logger.error({
      module: LoggerConstant.Firebase,
      funcName: 'Sign In With Custom Token Fail',
      error: { error },
    });
    throw error;
  }
};

export const getUserFirebase = () => {
  try {
    return auth?.currentUser;
  } catch (error) {
    throw error;
  }
};

export const signOutUser = async () => {
  try {
    if (auth?.currentUser) {
      Logger.info({
        module: LoggerConstant.Firebase,
        funcName: 'Sign Out User',
        params: {},
      });
      await signOut(auth);
      Logger.info({
        module: LoggerConstant.Firebase,
        funcName: 'Sign Out User Success',
        params: {},
      });
    }
  } catch (error) {
    Logger.error({
      module: LoggerConstant.Firebase,
      funcName: 'Sign Out User Fail',
      error: { error },
    });
    throw error;
  }
};
